import { LitElement, html, css } from 'lit';

export class CrusherTimelineItem extends LitElement {
  static styles = css`
    :host {
      --crusher-timeline-item-marker-col: calc(var(--crusher-spacing-6) + var(--crusher-spacing-1));
      --crusher-timeline-item-dot-size: var(--crusher-spacing-3);
      --crusher-timeline-item-line-width: var(--crusher-focus-width);
      --crusher-timeline-item-gap: var(--crusher-spacing-3);
      --crusher-timeline-item-line-color: var(--crusher-color-brand-primary);
      --crusher-timeline-item-line-opacity: 0.9;
      --crusher-timeline-item-dot-color: var(--crusher-color-brand-primary);
      --crusher-timeline-item-dot-border: var(--crusher-focus-width) solid var(--crusher-background-surface);
      --crusher-timeline-item-dot-top: var(--crusher-focus-offset);
      --crusher-timeline-item-line-top: calc(
        var(--crusher-timeline-item-dot-size) + var(--crusher-timeline-item-dot-top)
      );
      --crusher-timeline-item-padding-bottom: var(--crusher-spacing-8);

      display: grid;
      grid-template-columns: var(--crusher-timeline-item-marker-col) 1fr;
      column-gap: var(--crusher-timeline-item-gap);
      position: relative;
      padding-bottom: var(--crusher-timeline-item-padding-bottom);
    }

    .marker {
      grid-column: 1 / 2;
      position: relative;
      min-height: var(--crusher-timeline-item-dot-size);
    }
    .marker::before {
      content: "";
      position: absolute;
      left: calc((var(--crusher-timeline-item-marker-col) - var(--crusher-timeline-item-line-width)) / 2);
      top: var(--crusher-timeline-item-line-top);
      bottom: calc(-1 * var(--crusher-timeline-item-padding-bottom));
      width: var(--crusher-timeline-item-line-width);
      background: var(--crusher-timeline-item-line-color);
      opacity: var(--crusher-timeline-item-line-opacity);
    }

    :host(:last-child) .marker::before {
      display: none;
    }

    .dot {
      position: absolute;
      left: calc((var(--crusher-timeline-item-marker-col) - var(--crusher-timeline-item-dot-size)) / 2);
      top: var(--crusher-timeline-item-dot-top);
      width: var(--crusher-timeline-item-dot-size);
      height: var(--crusher-timeline-item-dot-size);
      border-radius: var(--crusher-radius-full);
      background: var(--crusher-timeline-item-dot-color);
      border: var(--crusher-timeline-item-dot-border);
      z-index: 1;
    }

    .content {
      grid-column: 2 / 3;
      min-width: 0;
    }

    ::slotted([slot="date"]) {
      display: inline-block;
      margin-bottom: var(--crusher-timeline-item-date-margin-bottom, var(--crusher-spacing-2));
      color: var(--crusher-timeline-item-date-color, var(--crusher-text-secondary));
      font-size: var(--crusher-timeline-item-date-size, var(--crusher-font-size-sm));
      font-weight: var(--crusher-timeline-item-date-weight, var(--crusher-font-weight-regular, 400));
    }

    :host([compact]) {
      --crusher-timeline-item-marker-col: var(--crusher-spacing-6);
      --crusher-timeline-item-dot-size: calc(var(--crusher-spacing-2) + var(--crusher-focus-offset));
      --crusher-timeline-item-padding-bottom: var(--crusher-spacing-6);
    }
    :host([flush]) {
      --crusher-timeline-item-marker-col: calc(var(--crusher-spacing-4) + var(--crusher-spacing-1));
      --crusher-timeline-item-dot-size: calc(var(--crusher-spacing-2) + var(--crusher-focus-offset));
    }

    :host([layout="split"]) {
      --crusher-timeline-item-split-icon-size: var(--crusher-spacing-10);
      --crusher-timeline-item-split-icon-top: calc(var(--crusher-spacing-4) + var(--crusher-focus-width));
      --crusher-timeline-item-split-icon-bg: var(--crusher-background-surface);
      --crusher-timeline-item-split-icon-border: var(--crusher-component-border-weight) solid var(--crusher-border-primary);
      --crusher-timeline-item-split-icon-color: var(--crusher-color-brand-primary);
      --crusher-timeline-item-split-content-padding: var(--crusher-spacing-8);
      --crusher-timeline-item-split-content-radius: var(--crusher-radius-md);
      --crusher-timeline-item-split-content-bg: transparent;
      --crusher-timeline-item-split-content-shadow: none;
      --crusher-timeline-item-split-connector-width: calc(var(--crusher-spacing-8) - var(--crusher-focus-width));
      --crusher-timeline-item-split-connector-height: var(--crusher-component-border-weight);
      --crusher-timeline-item-split-connector-top: calc(var(--crusher-spacing-10) - var(--crusher-spacing-1) + var(--crusher-component-border-weight));
      --crusher-timeline-item-split-connector-color: var(--crusher-border-primary);
      --crusher-timeline-item-split-gap: calc(var(--crusher-spacing-12) + var(--crusher-focus-width));

      display: block;
      box-sizing: border-box;
      padding: 0 var(--crusher-spacing-4);
      padding-bottom: var(--crusher-timeline-item-padding-bottom);
    }

    :host([layout="split"][side="start"]) {
      text-align: right;
    }

    :host([layout="split"]) .marker {
      position: absolute;
      left: calc(50% - (var(--crusher-timeline-item-split-icon-size) / 2));
      top: var(--crusher-timeline-item-split-icon-top);
      width: var(--crusher-timeline-item-split-icon-size);
      height: var(--crusher-timeline-item-split-icon-size);
      display: grid;
      place-items: center;
      border-radius: var(--crusher-radius-full);
      background: var(--crusher-timeline-item-split-icon-bg);
      border: var(--crusher-timeline-item-split-icon-border);
      color: var(--crusher-timeline-item-split-icon-color);
      z-index: 1;
    }

    :host([layout="split"]) .marker::before,
    :host([layout="split"]) .dot {
      display: none;
    }

    :host([layout="split"]) ::slotted([slot="icon"]) {
      display: inline-grid;
      place-items: center;
      color: inherit;
      font-size: var(--crusher-timeline-item-split-icon-font-size, var(--crusher-font-size-base));
    }

    :host([layout="split"]) .content {
      display: block;
      position: relative;
      box-sizing: border-box;
      width: calc(50% - var(--crusher-timeline-item-split-gap));
      padding: var(--crusher-timeline-item-split-content-padding);
      border-radius: var(--crusher-timeline-item-split-content-radius);
      background: var(--crusher-timeline-item-split-content-bg);
      box-shadow: var(--crusher-timeline-item-split-content-shadow);
    }

    :host([layout="split"][side="start"]) .content {
      margin-right: calc(50% + var(--crusher-timeline-item-split-gap));
    }

    :host([layout="split"][side="end"]) .content {
      margin-left: calc(50% + var(--crusher-timeline-item-split-gap));
    }

    :host([layout="split"]) .content::before {
      content: "";
      position: absolute;
      width: var(--crusher-timeline-item-split-connector-width);
      height: var(--crusher-timeline-item-split-connector-height);
      background: var(--crusher-timeline-item-split-connector-color);
      top: var(--crusher-timeline-item-split-connector-top);
      z-index: -1;
    }

    :host([layout="split"][side="start"]) .content::before {
      right: calc(-1 * var(--crusher-timeline-item-split-connector-width));
    }

    :host([layout="split"][side="end"]) .content::before {
      left: calc(-1 * var(--crusher-timeline-item-split-connector-width));
    }

    @media (max-width: 767px) {
      :host([layout="split"]),
      :host([layout="split"][side="start"]),
      :host([layout="split"][side="end"]) {
        padding-right: var(--crusher-timeline-item-split-mobile-padding-inline-end, var(--crusher-spacing-4));
        padding-left: var(
          --crusher-timeline-item-split-mobile-padding-inline-start,
          calc(var(--crusher-spacing-12) + var(--crusher-spacing-6) + var(--crusher-spacing-1) - var(--crusher-component-border-weight))
        );
        text-align: left;
      }

      :host([layout="split"]) .marker {
        left: var(--crusher-timeline-item-split-mobile-icon-left, calc(var(--crusher-spacing-4) - var(--crusher-component-border-weight)));
      }

      :host([layout="split"]) .content,
      :host([layout="split"][side="start"]) .content,
      :host([layout="split"][side="end"]) .content {
        width: auto;
        margin-right: 0;
        margin-left: 0;
      }

      :host([layout="split"][side="start"]) .content::before {
        left: calc(-1 * var(--crusher-timeline-item-split-connector-width));
        right: auto;
      }
    }
  `;

  render() {
    return html`
      <div class="marker"><slot name="icon"></slot><div class="dot"></div></div>
      <div class="content">
        <slot name="date"></slot>
        <slot></slot>
      </div>
    `;
  }
}

customElements.define('crusher-timeline-item', CrusherTimelineItem);
